/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Styles for the onboarding splash screen.
 *
 * @file OnboardingSplashStyles.js
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import { Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import mixins from '../../../assets/styles/mixins.scss';
import { createScopedClasses, createScopedStyles } from '../../../assets/styles/createScopedStyles';

const wordWrap = {
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  whiteSpace: 'normal'
};

const useOnboardingSplashStyles = {
  shell: {
    backgroundColor: mixins.lightThemeBgColor2,
    borderRadius: 24,
    padding: 16,
    width: '100%',
    boxSizing: 'border-box'
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%'
  },
  pageTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    letterSpacing: '-0.4px',
    color: mixins.color_text_primary,
    margin: 0,
    padding: '0 8px',
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
    ...wordWrap,
    '@media (min-width: 1024px)': {
      fontSize: 24,
      lineHeight: '32px',
      letterSpacing: '-0.48px'
    }
  },
  contentRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    width: '100%',
    '@media (min-width: 1024px)': {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: 8
    }
  },
  cardsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    minWidth: 0,
    flex: 1,
    '@media (min-width: 1024px)': {
      flex: '2 1 0'
    }
  },
  promotionColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    minWidth: 0,
    boxSizing: 'border-box',
    padding: '0 8px',
    '@media (min-width: 1024px)': {
      flex: '1 1 0',
      minWidth: 320,
      padding: 40,
      justifyContent: 'flex-start'
    }
  },
  promotionBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    width: '100%'
  },
  panelTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    letterSpacing: '-0.4px',
    margin: 0,
    ...wordWrap
  },
  promotionTitle: {
    color: mixins.color_text_primary
  },
  actionCardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    alignItems: 'stretch',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      minWidth: 0
    }
  },
  actionCard: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 auto',
    width: '100%',
    minWidth: 0,
    minHeight: 'unset',
    overflow: 'visible',
    padding: 24,
    borderRadius: 16,
    backgroundColor: mixins.chalkWhiteFont,
    boxSizing: 'border-box',
    '@media (min-width: 768px)': {
      flex: '1 1 0',
      width: 'auto',
      minHeight: 412
    },
    '@media (min-width: 1024px)': {
      flex: '1 1 0',
      minHeight: 412,
      height: 412
    }
  },
  actionCardBadge: {
    position: 'absolute',
    top: 24,
    right: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px 4px',
    borderRadius: 4,
    backgroundColor: mixins.badgeBgColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.amethyst
  },
  actionCardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    alignItems: 'center',
    width: '100%',
    flex: 1,
    minHeight: 0,
    '@media (min-width: 768px)': {
      height: '100%'
    }
  },
  actionCardHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    alignItems: 'center',
    width: '100%',
    flex: '0 1 auto',
    paddingTop: 28,
    textAlign: 'center',
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  actionCardIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: mixins.amethyst,
    backgroundColor: mixins.badgeBgColor,
    borderRadius: 1000,
    padding: 12,
    '& svg': {
      width: 40,
      height: 40
    }
  },
  actionCardTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    letterSpacing: '-0.4px',
    color: mixins.color_text_primary,
    margin: 0,
    width: '100%',
    ...wordWrap
  },
  actionCardDescription: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    color: mixins.color_text_primary,
    margin: 0,
    width: '100%',
    minHeight: 44,
    ...wordWrap
  },
  actionCardCta: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 1000,
    border: 'none',
    backgroundColor: `${mixins.amethyst} !important`,
    color: `${mixins.chalkWhiteFont} !important`,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    padding: '12px 20px',
    minHeight: 40,
    height: 'auto',
    flexShrink: 0,
    minWidth: 'unset',
    boxSizing: 'border-box',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    fontFeatureSettings: '\'liga\' off',
    boxShadow: 'none',
    '&.MuiButton-root': {
      minHeight: 40,
      padding: '12px 20px'
    },
    '&.MuiButton-contained': {
      minHeight: 40,
      padding: '12px 20px'
    },
    '& span': {
      display: 'inline-flex',
      alignItems: 'center',
      lineHeight: '16px',
      whiteSpace: 'nowrap'
    },
    '&:hover, &.MuiButton-contained:hover': {
      backgroundColor: `${mixins.amethystColorV2} !important`,
      boxShadow: 'none'
    },
    '&:focus-visible': {
      outline: `2px solid ${mixins.navyColor}`,
      outlineOffset: 2
    },
    '@media (min-width: 768px)': {
      marginTop: 'auto'
    }
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '0 12px',
    margin: 0,
    listStyle: 'none',
    width: '100%',
    boxSizing: 'border-box'
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8
  },
  featureIcon: {
    flexShrink: 0,
    display: 'flex',
    marginTop: 3,
    color: mixins.color_text_primary,
    '& svg': {
      width: 18,
      height: 18
    },
    '& svg path:last-child': {
      fill: mixins.chalkWhiteFont
    }
  },
  featureText: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: 400,
    lineHeight: '24px',
    color: mixins.color_text_primary,
    margin: 0,
    flex: 1,
    minWidth: 0,
    ...wordWrap
  },
  previewQuestionsText: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight400,
    lineHeight: '20px',
    color: mixins.color_text_primary,
    margin: 0,
    fontFeatureSettings: '\'liga\' off',
    ...wordWrap
  },
  previewQuestionsLink: {
    appearance: 'none',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    font: 'inherit',
    color: mixins.blueColor,
    textDecoration: 'underline',
    cursor: 'pointer',
    '&:focus-visible': {
      outline: `2px solid ${mixins.navyColor}`,
      outlineOffset: 2
    }
  },
  learnMoreButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 1000,
    border: `2px solid ${mixins.navyColor}`,
    color: mixins.navyColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    padding: '12px 20px',
    backgroundColor: 'transparent',
    alignSelf: 'flex-start',
    minWidth: 'unset',
    minHeight: 40,
    boxSizing: 'border-box',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    fontFeatureSettings: '\'liga\' off',
    '&.MuiButton-root': {
      minHeight: 40,
      padding: '12px 20px',
      boxSizing: 'border-box'
    },
    '&.MuiButton-outlined': {
      minHeight: 40,
      padding: '12px 20px'
    },
    '& span': {
      display: 'inline-flex',
      alignItems: 'center',
      whiteSpace: 'nowrap'
    },
    '&:hover, &.MuiButton-outlined:hover': {
      backgroundColor: mixins.secondaryButtonHoverColor,
      border: `2px solid ${mixins.navyColor}`
    },
    '&:focus-visible': {
      outline: `2px solid ${mixins.navyColor}`,
      outlineOffset: 2
    }
  }
};

export const classes = createScopedClasses('aet-onboarding-splash', useOnboardingSplashStyles);

export const scopedStyles = createScopedStyles(useOnboardingSplashStyles, classes);

export const OnboardingSplashStylesRoot = styled('div')(scopedStyles);

export const OnboardingActionCardCtaButton = styled(Button)({
  '&&': useOnboardingSplashStyles.actionCardCta
});

export const OnboardingLearnMoreButton = styled(Button)({
  '&&': useOnboardingSplashStyles.learnMoreButton
});

export default useOnboardingSplashStyles;
